import TitleBar from "../components/layout/TitleBar";
import Button from "../components/ui/Button";
import { useLocation } from "react-router";
import envVariables from "../data/envVariables";
import React, { useContext, useEffect, useState } from "react";
import MoreInfoTray from "../components/flight-brief/MoreInfoTray";
import LinkWithIcon from "../components/flight-brief/LinkWithIcon";
import PhotoCarousel from "../components/flight-brief/PhotoCarousel";
import Modal from "../components/ui/Modal";
import Share from "../components/flight-brief/Share";
import chevronDown from "../assets/icons/chevron-down-big.svg";
import chevronUp from "../assets/icons/chevron-up-big.svg";
import useGet from "../hooks/useGet";
import SEO from "../components/shared/SEO";
import ErrorModal from "../components/shared/ErrorModal";
import authContext from "../store/AuthContext";
import LazyLoadingContext from "../store/LazyLoadingContext";
import FlightBriefItinerary from "../components/flight-brief/FlightBriefItinerary";
import FlightBriefCrew from "../components/flight-brief/FlightBriefCrew";
import FlightBriefPassengers from "../components/flight-brief/FlightBriefPassengers";
import FlightBriefAirportInformation from "../components/flight-brief/FlightBriefAirportInformation";
import { getTripTypeLabelFromLegCount, normalizeTripTypeDisplay } from "../utilities/tripTypeDisplay";

const FlightBrief = () => {
    // Get params
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const view = queryParams.get("view");
    const legId = queryParams.get("lo");
    const isReportView = !!(view && view === "report");

    // Endpoints
    const briefUrl = `/api/frontend/final-briefing?legId=${legId}`;
    const downloadUrl = `${envVariables.documentBase}/api/invoice/final-brief?legId=${legId}&view=report`;

    // State
    const [allTrayStatus, setAllTrayStatus] = useState(isReportView ? "open" : "first-open");
    const [showSharePanel, setShowSharePanel] = useState(false);

    // Hooks
    const { error, loading: briefLoading, returnData: briefData } = useGet(briefUrl);
    const authCtx = useContext(authContext);
    const lazyLoadingCtx = useContext(LazyLoadingContext);

    useEffect(() => {
        return () => {
            lazyLoadingCtx.reset("flightBrief");
        };
        // Intentionally mount-only: re-firing would call reset() and clobber
        // page-ready state that other effects have already populated.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (briefData !== false && briefData !== "") {
            lazyLoadingCtx.updatePageCheck("flightBrief", {
                brief: true,
            });
        }
    }, [briefData, lazyLoadingCtx]);

    // Add PDF view when looking as report
    useEffect(() => {
        if (isReportView) {
            document.body.classList.add("pdf-view");
        }

        // Clean up
        return () => {
            document.body.classList.remove("pdf-view");
        };
    });

    const getCrewCount = () => {
        let count = 0;

        if (briefData.pilotName) {
            count++;
        }

        if (briefData.firstOfficerName) {
            count++;
        }

        if (briefData.flightAttendantName) {
            count++;
        }

        return count;
    };

    return (
        <>
            {!isReportView && (
                <>
                    <SEO title="Your Brief" />
                    {authCtx.token && (
                        <TitleBar title="Your Brief" cta="Dashboard" url={`/quote-dashboard/booking/${legId}`} />
                    )}
                </>
            )}

            <div className="page">
                {error ? (
                    <ErrorModal
                        title="Awaiting VIP terminal information"
                        errorMessage="We are waiting for the Operator to confirm the VIP terminals and will alert you when we have them"
                        ctaOptions={{
                            intro: "You can return to the dashboard here:",
                            url: `/quote-dashboard/booking/${legId}`,
                            text: "Dashboard",
                        }}
                    />
                ) : (
                    <>
                        {!lazyLoadingCtx.pageReady || briefLoading ? (
                            <>
                                <section className="wrap flight-brief flight-brief--skeleton">
                                    <div className="flight-brief__buttons">
                                        {!isReportView && (
                                            <Button
                                                classNames={["button--secondary"]}
                                                icon="download"
                                                onClick={() => {
                                                    window.open(downloadUrl, "_blank", "noreferrer");
                                                }}
                                            >
                                                Download
                                            </Button>
                                        )}
                                    </div>

                                    <div className="flight-brief__top-details">
                                        <div className="trip-detail">
                                            <div className="trip-detail__title">Trip number:</div>
                                            <div className="trip-detail__info">XXXX-XXXXX</div>
                                        </div>

                                        <div className="trip-detail">
                                            <div className="trip-detail__title">Date created:</div>
                                            <div className="trip-detail__info">
                                                XX/XX/XXXX <span className="trip-detail__info__light">at</span> XX:XX
                                            </div>
                                        </div>

                                        {!isReportView ? (
                                            <>
                                                {allTrayStatus !== "open" ? (
                                                    <button
                                                        onClick={() => setAllTrayStatus("open")}
                                                        className="flight-brief__expand-all"
                                                    >
                                                        Show all <img src={chevronDown} alt="" />
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => setAllTrayStatus("closed")}
                                                        className="flight-brief__expand-all"
                                                    >
                                                        Hide all <img src={chevronUp} alt="" />
                                                    </button>
                                                )}
                                            </>
                                        ) : null}
                                    </div>

                                    <MoreInfoTray />
                                    <MoreInfoTray />
                                    <MoreInfoTray />
                                    <MoreInfoTray />
                                    <MoreInfoTray />
                                    <MoreInfoTray />
                                </section>
                            </>
                        ) : (
                            briefData && (
                                <section className="wrap flight-brief">
                                    <div className="flight-brief__buttons">
                                        {/*<Button classNames={['button--secondary']} onClick={() => setShowSharePanel(true)} icon='share-dark'>Share</Button>*/}
                                        {!isReportView && (
                                            <Button
                                                classNames={["button--secondary"]}
                                                icon="download"
                                                onClick={() => {
                                                    window.open(downloadUrl, "_blank", "noreferrer");
                                                }}
                                            >
                                                Download
                                            </Button>
                                        )}
                                    </div>

                                    <div className="flight-brief__top-details">
                                        {briefData.tripNumber && (
                                            <div className="trip-detail">
                                                <div className="trip-detail__title">Trip number:</div>
                                                <div className="trip-detail__info">{briefData.tripNumber}</div>
                                            </div>
                                        )}

                                        {briefData.dateCreated && briefData.timeCreated && (
                                            <div className="trip-detail">
                                                <div className="trip-detail__title">Date created:</div>
                                                <div className="trip-detail__info">
                                                    {briefData.dateCreated}{" "}
                                                    <span className="trip-detail__info__light">at</span>{" "}
                                                    {briefData.timeCreated}
                                                </div>
                                            </div>
                                        )}

                                        {(() => {
                                            const tripTypeLabel =
                                                normalizeTripTypeDisplay(briefData.tripType) ||
                                                getTripTypeLabelFromLegCount(briefData.legs?.length);
                                            if (!tripTypeLabel) return null;
                                            return (
                                                <div className="trip-detail">
                                                    <div className="trip-detail__title">Trip type:</div>
                                                    <div className="trip-detail__info">{tripTypeLabel}</div>
                                                </div>
                                            );
                                        })()}

                                        {!isReportView ? (
                                            <>
                                                {allTrayStatus === "closed" ? (
                                                    <button
                                                        onClick={() => setAllTrayStatus("open")}
                                                        className="flight-brief__expand-all"
                                                    >
                                                        Show all <img src={chevronDown} alt="" />
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => setAllTrayStatus("closed")}
                                                        className="flight-brief__expand-all"
                                                    >
                                                        Hide all <img src={chevronUp} alt="" />
                                                    </button>
                                                )}
                                            </>
                                        ) : null}
                                    </div>

                                    <MoreInfoTray
                                        buttonText="Aircraft"
                                        open={allTrayStatus === "open" || allTrayStatus === "first-open"}
                                        isReportView={isReportView}
                                    >
                                        {briefData.aircraftTailNumber && briefData.aircraft.aircraftName && (
                                            <>
                                                <div className="flight-brief__plane-type">Type</div>
                                                {briefData.aircraftTailNumber} ({briefData.aircraft.aircraftName})
                                            </>
                                        )}

                                        <div className="flight-brief__aircraft-wrapper">
                                            {briefData.aircraft.photoData && (
                                                <div className="flight-brief__photos-wrapper">
                                                    <PhotoCarousel
                                                        photoData={briefData.aircraft.photoData}
                                                        enabled={!isReportView}
                                                    />
                                                </div>
                                            )}

                                            {briefData.aircraft.floorplan && (
                                                <img
                                                    src={briefData.aircraft.floorplan[0].url}
                                                    alt={briefData.aircraft.floorplan[0].alt}
                                                    className="flight-brief__blueprint"
                                                />
                                            )}
                                        </div>
                                    </MoreInfoTray>

                                    <MoreInfoTray
                                        buttonText="Trip Itinerary"
                                        open={allTrayStatus === "open"}
                                        isReportView={isReportView}
                                    >
                                        <FlightBriefItinerary briefData={briefData} />
                                    </MoreInfoTray>

                                    <MoreInfoTray
                                        buttonText={`Crew (${getCrewCount()})`}
                                        open={allTrayStatus === "open"}
                                        isReportView={isReportView}
                                    >
                                        <FlightBriefCrew briefData={briefData} />
                                    </MoreInfoTray>

                                    <MoreInfoTray
                                        buttonText={"Passengers (" + briefData.numberPax + ")"}
                                        open={allTrayStatus === "open"}
                                        isReportView={isReportView}
                                    >
                                        <FlightBriefPassengers briefData={briefData} />
                                    </MoreInfoTray>

                                    <MoreInfoTray
                                        buttonText="Airport Information"
                                        open={allTrayStatus === "open"}
                                        isReportView={isReportView}
                                    >
                                        <FlightBriefAirportInformation briefData={briefData} />
                                    </MoreInfoTray>

                                    <MoreInfoTray
                                        buttonText="Contact Details"
                                        open={allTrayStatus === "open"}
                                        isReportView={isReportView}
                                    >
                                        <div className="flight-brief__content-grid">
                                            <div>
                                                <div className="flight-brief__item">
                                                    <div className="flight-brief__dark-title">
                                                        SHY Flight Management team
                                                    </div>
                                                </div>
                                                <LinkWithIcon link="tel:44+2086168821" icon="phone">
                                                    +44 20 8616 8821
                                                </LinkWithIcon>
                                                <br />
                                                <LinkWithIcon link="mailto:ops@shyaviation.com" icon="email">
                                                    ops@shyaviation.com
                                                </LinkWithIcon>
                                                <br />
                                                <LinkWithIcon
                                                    target="_blank"
                                                    link="https://shyaviation.com"
                                                    icon="website"
                                                >
                                                    shyaviation.com
                                                </LinkWithIcon>
                                            </div>
                                        </div>
                                    </MoreInfoTray>

                                    {briefData.additionalItems.length > 0 && (
                                        <MoreInfoTray
                                            buttonText="Additional Information"
                                            open={allTrayStatus === "open"}
                                            isReportView={isReportView}
                                        >
                                            {briefData.additionalItems.map((item, itemId) => {
                                                return <p key={itemId}>{item}</p>;
                                            })}
                                        </MoreInfoTray>
                                    )}
                                </section>
                            )
                        )}
                        <Modal
                            classNames={["modal__content--thin"]}
                            modalVisible={showSharePanel}
                            closeModal={() => {
                                setShowSharePanel(false);
                            }}
                        >
                            {/* TODO add endpoint & ref below */}
                            <Share
                                endpoint={"/api/share-flight-brief"}
                                shareRef={"some sort of ref"}
                                close={() => {
                                    setShowSharePanel(false);
                                }}
                            />
                        </Modal>
                    </>
                )}
            </div>
        </>
    );
};

export default FlightBrief;
